<template>
  <DialogBase
    :title="dialogTitle"
    :visible="dialogVisible"
    size="xl"
    top="5vh"
    :use-panel="false"
    :show-confirm="false"
    cancel-text="Đóng"
    @cancel="closeDialog"
    @before-close="closeDialog"
  >
    <div class="sk-dialog-content">
      <div class="sk-panel">
        <div class="sk-panel-head">
          <div class="sk-panel-icon">
            <i class="el-icon-time" />
          </div>
          <div class="sk-panel-info">
            <p class="sk-panel-title">Lịch sử trạng thái &amp; vòng đời nhân sự Plus</p>
            <p class="sk-panel-subtitle">
              <span>Họ tên: <strong class="text-slate-800">{{ employeeName }}</strong></span>
              <span class="info-divider">•</span>
              <span>Mã NV: <strong class="text-slate-800">{{ employeeCode }}</strong></span>
              <template v-if="employeePhone">
                <span class="info-divider">•</span>
                <span>SĐT: <strong class="text-slate-800">{{ employeePhone }}</strong></span>
              </template>
            </p>
          </div>
          <div class="lifecycle-summary-badge">
            <span>Tổng cộng: <strong>{{ total }}</strong> bản ghi</span>
          </div>
        </div>

        <div class="lifecycle-toolbar">
          <div class="lifecycle-filter-grid">
            <div class="lifecycle-filter-item">
              <label class="lifecycle-filter-label">Loại sự kiện</label>
              <el-select
                v-model="filters.eventType"
                clearable
                placeholder="Tất cả sự kiện"
                size="small"
                class="lifecycle-select"
                @change="filterChanged"
              >
                <el-option
                  v-for="item in eventTypes"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </div>

            <div class="lifecycle-filter-item">
              <label class="lifecycle-filter-label">Từ ngày</label>
              <el-date-picker
                v-model="filters.fromDate"
                type="date"
                size="small"
                value-format="yyyy-MM-dd"
                format="dd-MM-yyyy"
                clearable
                placeholder="Chọn từ ngày"
                class="lifecycle-date-picker"
                @change="filterChanged"
              />
            </div>

            <div class="lifecycle-filter-item">
              <label class="lifecycle-filter-label">Đến ngày</label>
              <el-date-picker
                v-model="filters.toDate"
                type="date"
                size="small"
                value-format="yyyy-MM-dd"
                format="dd-MM-yyyy"
                clearable
                placeholder="Chọn đến ngày"
                class="lifecycle-date-picker"
                @change="filterChanged"
              />
            </div>

            <div class="lifecycle-filter-item lifecycle-filter-item--action">
              <el-button
                size="small"
                plain
                icon="el-icon-refresh"
                @click="resetFilters"
              >
                Đặt lại
              </el-button>
            </div>
          </div>
        </div>

        <div class="lifecycle-table-wrapper sk-u-mt-12">
          <el-table
            v-loading="loading"
            :data="events"
            border
            stripe
            class="sk-table"
            max-height="460"
            empty-text="Chưa có lịch sử nhân sự"
            style="width: 100%"
          >
            <el-table-column
              label="STT"
              type="index"
              width="50"
              align="center"
            />
            <el-table-column label="Ngày hiệu lực" width="130" align="center">
              <template slot-scope="{ row }">
                <span class="font-medium text-slate-800">{{ formatDate(row.effectiveDate) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Sự kiện" width="165" align="center">
              <template slot-scope="{ row }">
                <el-tag size="small" :type="tagType(row.eventType)">{{ eventLabel(row.eventType) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Nội dung thay đổi" min-width="320" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <span>{{ description(row) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Người thao tác" width="170" align="center">
              <template slot-scope="{ row }">
                <span>{{ row.actorName || 'Hệ thống' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Nguồn" width="125" align="center">
              <template slot-scope="{ row }">
                <el-tag size="mini" type="info" effect="plain">{{ sourceLabel(row.source) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Lúc thao tác" width="160" align="center">
              <template slot-scope="{ row }">
                <span class="text-slate-500">{{ formatDateTime(row.createdDate) }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="lifecycle-pagination">
          <el-pagination
            background
            :current-page="pageNumber"
            :page-size="maxPageItem"
            :page-sizes="$pageListDefaultNew || [10, 20, 50, 100]"
            :total="total"
            layout="total, sizes, prev, pager, next"
            @size-change="sizeChanged"
            @current-change="pageChanged"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="sk-dialog__footer">
        <el-button class="btn-cancel" plain @click="closeDialog">Đóng</el-button>
      </div>
    </template>
  </DialogBase>
</template>

<script>
import moment from "moment";
import DialogBase from "@/components/DialogBase.vue";
import EmployeeLifecycleService from "@/services/EmployeeService/EmployeeLifecycleService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";

export default {
  name: "EmployeeLifecyclePlusDialog",
  components: {
    DialogBase,
  },
  props: {
    dialogVisible: {
      type: Boolean,
      default: false,
    },
    employee: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      requestVersion: 0,
      events: [],
      total: 0,
      pageNumber: 1,
      maxPageItem: this.$pageSizeDefaultNew || 10,
      filters: {
        eventType: "",
        fromDate: "",
        toDate: "",
      },
      eventTypes: [
        { value: "CREATED", label: "Tạo hồ sơ" },
        { value: "STATUS_CHANGED", label: "Đổi trạng thái" },
        { value: "STATUS_DATE_CORRECTED", label: "Sửa ngày trạng thái" },
        { value: "GROUP_OUT", label: "Nghỉ hẳn" },
        { value: "DELETED", label: "Xóa hồ sơ" },
        { value: "RESTORED", label: "Khôi phục" },
      ],
    };
  },
  computed: {
    dialogTitle() {
      const name = this.employee && this.employee.fullName ? ` - ${this.employee.fullName}` : "";
      return `Lịch sử nhân sự Plus${name}`;
    },
    employeeCode() {
      return (
        (this.employee && (this.employee.code || this.employee.employeeCode)) ||
        (this.events[0] && this.events[0].employeeCode) ||
        "-"
      );
    },
    employeeName() {
      return (this.employee && this.employee.fullName) || "-";
    },
    employeePhone() {
      return (this.employee && (this.employee.phone || this.employee.phoneSMS)) || "";
    },
  },
  watch: {
    dialogVisible(visible) {
      if (visible) {
        this.resetFilters(false);
        this.loadHistory();
      } else {
        this.requestVersion += 1;
        this.loading = false;
        this.events = [];
        this.total = 0;
      }
    },
  },
  methods: {
    async loadHistory() {
      const idInfoEmployee = this.employee && (this.employee.idInfoEmployee || this.employee.id);
      if (!idInfoEmployee) return;

      const requestVersion = ++this.requestVersion;
      this.loading = true;
      try {
        const params = {
          idInfoEmployee,
          pageNumber: this.pageNumber,
          maxPageItem: this.maxPageItem,
          eventType: this.filters.eventType || undefined,
          fromDate: this.filters.fromDate || undefined,
          toDate: this.filters.toDate || undefined,
        };
        const response = await EmployeeLifecycleService.searchAdmin(params);
        if (requestVersion !== this.requestVersion) return;
        const payload = response && response.data ? response.data : {};
        this.events = Array.isArray(payload.data) ? payload.data : [];
        this.total = Number(payload.total) || 0;
      } catch (error) {
        if (requestVersion !== this.requestVersion) return;
        this.events = [];
        this.total = 0;
        NotifyMessage.leftCornerNotificationError(
          error?.response?.data?.message || "Không tải được lịch sử nhân viên Plus"
        );
      } finally {
        if (requestVersion === this.requestVersion) this.loading = false;
      }
    },
    filterChanged() {
      if (
        this.filters.fromDate &&
        this.filters.toDate &&
        this.filters.fromDate > this.filters.toDate
      ) {
        this.$message({
          message: "Từ ngày không được lớn hơn đến ngày",
          type: "warning",
        });
        return;
      }
      this.pageNumber = 1;
      this.loadHistory();
    },
    resetFilters(reload = true) {
      this.filters = { eventType: "", fromDate: "", toDate: "" };
      this.pageNumber = 1;
      if (reload) this.loadHistory();
    },
    sizeChanged(size) {
      this.maxPageItem = size;
      this.pageNumber = 1;
      this.loadHistory();
    },
    pageChanged(page) {
      this.pageNumber = page;
      this.loadHistory();
    },
    closeDialog(done) {
      this.$emit("dialog-close");
      if (typeof done === "function") done();
    },
    eventLabel(type) {
      const item = this.eventTypes.find((x) => x.value === type);
      return item ? item.label : type || "Sự kiện khác";
    },
    tagType(type) {
      const map = {
        CREATED: "success",
        STATUS_CHANGED: "warning",
        STATUS_DATE_CORRECTED: "info",
        GROUP_OUT: "danger",
        DELETED: "danger",
        RESTORED: "success",
      };
      return map[type] || "info";
    },
    description(event) {
      if (event.eventType === "CREATED") {
        return `Trạng thái ban đầu: ${event.newStatus || "-"}`;
      }
      if (event.eventType === "STATUS_DATE_CORRECTED") {
        return `${event.newStatus || "-"}: ${this.formatDate(
          event.oldEffectiveDate
        )} → ${this.formatDate(event.effectiveDate)}`;
      }
      if (event.eventType === "GROUP_OUT") {
        return `${event.oldStatus || "-"} → ${event.newStatus || "-"}; nhóm ${
          event.newGroupOutName || "-"
        }`;
      }
      if (event.eventType === "DELETED") {
        return `Xóa hồ sơ từ trạng thái ${event.oldStatus || "-"}`;
      }
      if (event.eventType === "RESTORED") {
        return `Khôi phục: ${event.oldStatus || "-"} → ${event.newStatus || "-"}`;
      }
      return `${event.oldStatus || "-"} → ${event.newStatus || "-"}`;
    },
    sourceLabel(source) {
      const map = {
        WEB: "Web",
        WEB_SUPPER_PLUS: "SupperPlus",
        MOBILE_PLUS: "Mobile Plus",
        EXCEL: "Excel",
        ADMIN: "Quản trị",
        ACCOUNT_MERGE: "Gộp tài khoản",
        BACKFILL: "Dữ liệu cũ",
      };
      return map[source] || source || "-";
    },
    formatDate(value) {
      return value ? moment(value).format("DD-MM-YYYY") : "-";
    },
    formatDateTime(value) {
      return value ? moment(value).format("DD-MM-YYYY HH:mm") : "-";
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog.scss";
@import "@/assets/style/sk-table.scss";

.info-divider {
  margin: 0 8px;
  color: #cbd5e1;
}

.lifecycle-summary-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;

  strong {
    font-size: 15px;
    font-weight: 700;
    margin: 0 3px;
  }
}

.lifecycle-toolbar {
  margin: 14px 0 16px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.lifecycle-filter-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}

.lifecycle-filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;

  &--action {
    margin-left: auto;
  }
}

.lifecycle-filter-label {
  font-size: 12.5px;
  color: #475569;
  font-weight: 600;
  line-height: 1.2;
}

.lifecycle-select {
  width: 190px;
}

.lifecycle-date-picker {
  width: 170px;
}

.lifecycle-table-wrapper {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.lifecycle-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

/* Override table header blue as per guidelines section 10.4 */
/deep/ .sk-table thead th,
/deep/ .sk-table thead td {
  background: #2563eb !important;
  color: #ffffff !important;
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

/deep/ .sk-table thead th > .cell,
/deep/ .sk-table thead td > .cell {
  color: #ffffff !important;
  font-weight: 700;
}

/deep/ .sk-table thead tr {
  height: 48px;
}

/deep/ .sk-table tbody td {
  background: #ffffff;
}

/deep/ .el-input__inner,
/deep/ .el-select .el-input__inner {
  border-radius: 10px;
}

/deep/ .el-button {
  border-radius: 10px;
}

@media (max-width: 768px) {
  .sk-panel-head {
    flex-direction: column;
  }

  .lifecycle-summary-badge {
    align-self: flex-start;
  }

  .lifecycle-filter-grid {
    flex-direction: column;
    align-items: stretch;
  }

  .lifecycle-filter-item--action {
    margin-left: 0;
  }

  .lifecycle-select,
  .lifecycle-date-picker {
    width: 100%;
  }

  .lifecycle-pagination {
    justify-content: center;
  }
}
</style>
